import {
  collection,
  getDocs,
  writeBatch,
  doc,
} from 'firebase/firestore';
import { db } from './firebase';

interface InitializationProgress {
  stage: string;
  message: string;
  progress: number;
}

interface InitializationResult {
  success: boolean;
  message: string;
  summary?: {
    deletedRecords: { [collection: string]: number };
    createdRecords: number;
    errors: string[];
  };
}

export class SystemInitializationService {
  
  /**
   * Complete system initialization - cleanup only (no CSV import)
   */
  static async initializeSystem(
    onProgress?: (progress: InitializationProgress) => void
  ): Promise<InitializationResult> {
    const startTime = Date.now();
    const summary = {
      deletedRecords: {} as { [collection: string]: number },
      createdRecords: 0,
      errors: [] as string[]
    };

    try {
      // Stage 1: Delete transactional data
      onProgress?.({
        stage: 'cleanup',
        message: 'Deleting transactional data...',
        progress: 10
      });

      const deletionResult = await this.deleteTransactionalData();
      summary.deletedRecords = deletionResult.deletedCounts;
      
      if (!deletionResult.success) {
        summary.errors.push(`Cleanup failed: ${deletionResult.message}`);
      }

      // Stage 2: Complete
      onProgress?.({
        stage: 'complete',
        message: 'System cleanup complete!',
        progress: 100
      });

      const duration = Math.round((Date.now() - startTime) / 1000);
      const successMessage = `✅ System Cleanup Completed

🗑️ Deleted Records:
${Object.entries(summary.deletedRecords)
  .map(([collection, count]) => `  • ${collection}: ${count}`)
  .join('\n')}

⏱️ Completed in ${duration} seconds`;

      return {
        success: true,
        message: successMessage,
        summary
      };

    } catch (error) {
      console.error('System initialization failed:', error);
      
      const errorMessage = `❌ System Initialization Failed

Error: ${error instanceof Error ? error.message : 'Unknown error'}

⚠️ System may be in inconsistent state. Please check manually.`;

      return {
        success: false,
        message: errorMessage,
        summary
      };
    }
  }

  /**
   * Delete all transactional data while preserving master data
   */
  static async deleteTransactionalData(): Promise<{
    success: boolean;
    message: string;
    deletedCounts: { [collection: string]: number };
  }> {
    // Exclude 'beats' from cleanup as per new requirements
    const transactionalCollections = [
      'inventory_journal',
      'beat_loadout', // legacy singular
      'beat_loadouts', // some paths use plural
      'outletVisits',
      'salesDetails',
      'salesHeaders',
      'route_daily_status',
      'vehicle_daily_status',
      'inventory',
      // 'beats', // excluded
      'settlements',
      'retailer_sales',
      'zoho_sync_status'
    ];

    const deletedCounts: { [collection: string]: number } = {};
    const errors: string[] = [];

    try {
      console.log('🗑️ Starting transactional data cleanup...');

      for (const collectionName of transactionalCollections) {
        try {
          console.log(`🗑️ Deleting ${collectionName}...`);
          
          // Get all documents in the collection
          const snapshot = await getDocs(collection(db, collectionName));
          deletedCounts[collectionName] = snapshot.size;
          
          if (snapshot.size === 0) {
            console.log(`  • ${collectionName}: Already empty`);
            continue;
          }

          // Delete in batches to avoid Firebase limits
          const batchSize = 500;
          const batches: ReturnType<typeof writeBatch>[] = [];

          for (let i = 0; i < snapshot.docs.length; i += batchSize) {
            const batch = writeBatch(db);
            const batchDocs = snapshot.docs.slice(i, i + batchSize);
            
            batchDocs.forEach(docSnapshot => {
              batch.delete(docSnapshot.ref);
            });
            
            batches.push(batch);
          }

          // Execute all batches
          await Promise.all(batches.map(batch => batch.commit()));
          
          console.log(`  • ${collectionName}: Deleted ${snapshot.size} records`);
          
        } catch (error) {
          const errorMsg = `Failed to delete ${collectionName}: ${error}`;
          console.error(errorMsg);
          errors.push(errorMsg);
          deletedCounts[collectionName] = 0;
        }
      }

      // Seed baseline inventory records for two main warehouses and two products
      try {
        const seedPairs = [
          { productId: 'PRD001', warehouseId: 'MUM-WH01' },
          { productId: 'PRD002', warehouseId: 'MUM-WH01' },
          { productId: 'PRD001', warehouseId: 'MUM-WH02' },
          { productId: 'PRD002', warehouseId: 'MUM-WH02' },
        ];
        const seedFields = {
          openingStock: 0,
          inwardStock: 100,
          outwardStock: 0,
          closingStock: 100,
          warehouseType: 'main',
          lastUpdated: new Date().toISOString(),
        } as any;
        const seedBatch = writeBatch(db);
        seedPairs.forEach(({ productId, warehouseId }) => {
          const id = `${warehouseId}_${productId}`; // deterministic id
          const ref = doc(db, 'inventory', id);
          seedBatch.set(ref, { productId, warehouseId, ...seedFields }, { merge: true } as any);
        });
        await seedBatch.commit();
        console.log('  • inventory: Seeded 4 baseline records for MUM-WH01/MUM-WH02 and PRD001/PRD002');
      } catch (e) {
        const msg = `Failed to seed baseline inventory docs: ${e}`;
        console.error(msg);
        errors.push(msg);
      }

      const totalDeleted = Object.values(deletedCounts).reduce((sum, count) => sum + count, 0);
      
      if (errors.length > 0) {
        return {
          success: false,
          message: `Cleanup completed with errors: ${errors.join(', ')}`,
          deletedCounts
        };
      }

      return {
        success: true,
        message: `Successfully deleted ${totalDeleted} transactional records`,
        deletedCounts
      };

    } catch (error) {
      console.error('Cleanup failed:', error);
      return {
        success: false,
        message: `Cleanup failed: ${error}`,
        deletedCounts
      };
    }
  }
  
  /**
   * Validate system state after initialization
   */
  static async validateSystemState(): Promise<{
    isValid: boolean;
    issues: string[];
    summary: { [collection: string]: number };
  }> {
    const issues: string[] = [];
    const summary: { [collection: string]: number } = {};

    try {
      // Check that transactional collections are empty (beats excluded)
      const transactionalCollections = [
        'inventory_journal', 'beat_loadout', 'beat_loadouts', 'outletVisits',
        'salesDetails', 'salesHeaders', 'route_daily_status', 'vehicle_daily_status', 'inventory',
        'settlements', 'retailer_sales', 'zoho_sync_status'
      ];

      for (const collectionName of transactionalCollections) {
        const snapshot = await getDocs(collection(db, collectionName));
        summary[collectionName] = snapshot.size;
        
        if (snapshot.size > 0) {
          issues.push(`${collectionName} should be empty but has ${snapshot.size} records`);
        }
      }

      // Check that master data collections have records
      const masterCollections = ['products', 'warehouses', 'users', 'outlets'];
      
      for (const collectionName of masterCollections) {
        const snapshot = await getDocs(collection(db, collectionName));
        summary[collectionName] = snapshot.size;
        
        if (snapshot.size === 0) {
          issues.push(`${collectionName} is empty - master data missing`);
        }
      }

      return {
        isValid: issues.length === 0,
        issues,
        summary
      };

    } catch (error) {
      return {
        isValid: false,
        issues: [`Validation failed: ${error}`],
        summary
      };
    }
  }
}
